<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--titlegroup">
        <span class="a-header--title slide-down-enter-active">Aggregated Alerts Overview</span>
        <HelpButton :content="HELP_CONTENT.overview" />
      </span>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <transition name="slide-up">
      <div v-if="!isLoading" class="a-filter">
        <a-row class="full-w" type="flex" justify="start" :gutter="24">
          <a-col :span="8">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.from && !!filterParams.to }">
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-range-picker
                :ranges="RANGE"
                :disabledDate="disabledDate"
                :placeholder="['From', 'To']"
                :format="DATE_FORMAT.DDMMMYYYY"
                @change="onChangeDate"
              />
            </div>
          </a-col>
          <a-col :span="16">
            <p class="ce-note mb-0">
              Showing {{ periodLabel }}. Times are shown in your account time zone. You can look back up to three months.
            </p>
          </a-col>
        </a-row>
      </div>
    </transition>
    <div v-if="!isLoading" class="a-body flex-grow-1 overflow-y-auto p-20-px">
      <!-- The pipeline strip. Read left to right. Boxes 1 and 2 are about the
           dashboard, boxes 3 and 4 are about the inbox, so the last two are
           tinted. The bold line underneath says why that matters. -->
      <div class="ce-pipe">
        <div class="ce-pipe__step">
          <div class="ce-pipe__n">1 · Alerts</div>
          <div class="ce-pipe__v">
            {{ formatNumber(summary.alerts) }}
            <small>{{ perDayLabel(summary.alerts) }}</small>
          </div>
          <div class="ce-pipe__d">One for each changed page. One real change can appear here hundreds of times.</div>
        </div>
        <div class="ce-pipe__step">
          <div class="ce-pipe__n">2 · Grouped alerts</div>
          <div class="ce-pipe__v">
            {{ formatNumber(summary.events) }}
            <small>{{ ratioLabel }}</small>
          </div>
          <div class="ce-pipe__d">
            Alerts that came from the same change, put in one row. Every group opens into the alerts inside it.
          </div>
        </div>
        <div class="ce-pipe__step ce-pipe__step--inbox">
          <div class="ce-pipe__n">3 · Site-wide incidents</div>
          <div class="ce-pipe__v">
            {{ formatNumber(summary.site_incidents) }}
            <small>on {{ formatNumber(summary.sites_affected) }} website{{ Number(summary.sites_affected) === 1 ? '' : 's' }}</small>
          </div>
          <div class="ce-pipe__d">
            A website with potential defacement, or a website that is unreachable. The rule is in Settings.
          </div>
        </div>
        <div class="ce-pipe__step ce-pipe__step--inbox">
          <div class="ce-pipe__n">4 · Messages</div>
          <div class="ce-pipe__v ce-pipe__v--pending">Not counted yet</div>
          <div class="ce-pipe__d">
            The messages sent to an inbox. This count is not available yet.
          </div>
        </div>
      </div>
      <p class="ce-pipenote">
        <strong>Boxes 1 and 2 shorten the dashboard. Boxes 3 and 4 reduce the messages sent. The two are separate.</strong>
        The sending rules count alerts for each website. They never read the groups.
      </p>

      <!-- This card is about the Alerts list only. Grouping never removes a
           row from it. Whether an alert is also emailed is decided by the
           notification mode in Settings, and this screen does not read that
           setting, so it must not make a claim about email. -->
      <div class="ce-safecard">
        <p class="ce-cardtitle">Alert Coverage</p>
        <div class="ce-safecard__big">
          0
          <small>HIGH alerts removed from the Alerts list</small>
        </div>
        <p class="ce-caption">{{ safeCaption }}</p>
      </div>

      <div class="ce-panel">
        <p class="ce-cardtitle">Alert Trend</p>
        <!-- The switches double as the legend, so there is one place to read the
             colours and one place to turn a severity off. Switching hides a
             band; it never asks the API again. -->
        <SeverityToggle :value="visibleSeverities" :counts="severityCounts" @change="onSeverityChange" />
        <template v-if="byDay.length">
          <p v-if="noSeveritySelected" class="ce-empty">No severity is selected, so nothing is drawn. Turn one back on above.</p>
          <template v-else>
            <p class="ce-panellabel">
              WITHOUT GROUPING: <b>{{ formatNumber(shownAlerts) }}</b> alert rows
            </p>
            <div class="ce-chart ce-chart--tall">
              <BarChart :chartData="alertsChartData" :options="chartOptions" :styles="fillStyles" />
            </div>
            <p class="ce-panellabel">
              WITH GROUPING: <b>{{ formatNumber(shownEvents) }}</b> grouped alerts
            </p>
            <div class="ce-chart">
              <BarChart :chartData="eventsChartData" :options="chartOptions" :styles="fillStyles" />
            </div>
          </template>
        </template>
        <p v-else class="ce-empty">No alerts in this period.</p>
      </div>

      <a-row :gutter="16" class="mt-3">
        <a-col :span="8">
          <div class="ce-chart-card">
            <WidgetCard title="Severity in this period" icon="pie-chart" :loading="false" :empty="severityEmpty">
              <ul class="ce-sevlist">
                <li v-for="row in severityRows" :key="row.key" class="ce-sevlist__item">
                  <span class="ce-sevlist__dot" :style="{ background: row.color }" />
                  <span class="ce-sevlist__label">{{ row.label }}</span>
                  <span class="ce-sevlist__value">{{ formatNumber(row.count) }}</span>
                </li>
              </ul>
            </WidgetCard>
          </div>
        </a-col>
        <a-col :span="16">
          <div class="ce-chart-card">
            <WidgetCard title="The biggest changes in this period" icon="unordered-list" :loading="false" :empty="topEmpty">
              <ul class="ce-toplist">
                <li v-for="row in topEvents" :key="row.id" class="ce-toplist__item">
                  <div class="ce-toplist__line">
                    <SeverityMix compact :severity="row.severity" :mix="row.severityMix" />
                    <router-link class="ce-toplist__link" :to="'/change-events/detail/' + row.id">
                      {{ row.summary }}
                    </router-link>
                    <span class="ce-toplist__count">
                      <b>{{ formatNumber(row.alertCount) }}</b> alerts in 1 group
                    </span>
                  </div>
                  <div class="ce-toplist__sub">{{ row.site }} · {{ row.categoryLabel }} · {{ formatNumber(row.pageCount) }} pages</div>
                  <div class="ce-toplist__track">
                    <div class="ce-toplist__fill" :style="{ width: row.barWidth }" />
                  </div>
                </li>
              </ul>
            </WidgetCard>
          </div>
        </a-col>
      </a-row>

      <p class="ce-foot">
        A group closes after the quiet period set in
        <router-link to="/change-events/settings">Settings</router-link>. A group carries the severity of its most serious alert.
      </p>
    </div>
  </div>
</template>

<script>
  import moment from 'moment-timezone';
  import { DATE_FORMAT } from '@constants/dateFormat';
  import BarChart from '@shared/components/BarChart';
  import WidgetCard from '@modules/Home/components/dashboard/WidgetCard.vue';
  import ChangeEventsService from '../services/ChangeEvents.service';
  import severityFilter from '../mixins/severityFilter.mixin';
  import utcDateRange from '../mixins/utcDateRange.mixin';
  import {
    SEVERITY_STACK_ORDER,
    SEVERITY_SERIES,
    SEVERITY_COLORS,
    categoryLabel,
    severityMix,
    formatNumber,
    ratioLabel
  } from '../consts/ChangeEvents.consts';
  import { HELP_CONTENT } from '../consts/helpContent';
  import HelpButton from './HelpButton.vue';
  import SeverityMix from './SeverityMix.vue';

  export default {
    name: 'ChangeEventsOverview',
    components: { BarChart, WidgetCard, HelpButton, SeverityMix },
    mixins: [severityFilter, utcDateRange],
    data() {
      return {
        HELP_CONTENT,
        DATE_FORMAT,
        isLoading: true,
        summary: {
          alerts: 0,
          events: 0,
          ratio: 0,
          site_incidents: 0,
          sites_affected: 0,
          by_day: [],
          by_severity: {},
          top_events: []
        },
        filterParams: {
          from: null,
          to: null,
          // Kept only so the note under the picker can name the period.
          fromLabel: null,
          toLabel: null
        },
        // Fill .ce-chart so the canvas is flex-sized (shrinks and grows).
        fillStyles: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }
      };
    },
    computed: {
      periodLabel() {
        if (this.filterParams.fromLabel && this.filterParams.toLabel) {
          return `${this.filterParams.fromLabel} to ${this.filterParams.toLabel}`;
        }
        return 'the last 7 days';
      },
      byDay() {
        return Array.isArray(this.summary.by_day) ? this.summary.by_day : [];
      },
      dayLabels() {
        return this.byDay.map((d) => moment(d.date || d.day).format('D MMM'));
      },
      /** "What you saw": one bar for each day, alerts split by severity. */
      alertsChartData() {
        return this.buildStack('alerts');
      },
      /** "What you would see": the same days, aggregated alerts by severity. */
      eventsChartData() {
        return this.buildStack('events');
      },
      /**
       * How many alerts each severity has in this period. Feeds the switches, so
       * a severity with nothing in it is drawn faint instead of disappearing.
       */
      severityCounts() {
        const source = this.summary.by_severity || {};
        const counts = {};
        SEVERITY_STACK_ORDER.forEach((key) => {
          counts[key] = this.pickSeverity(source, key);
        });
        return counts;
      },
      /** The alert total behind the top chart, after the severity switches. */
      shownAlerts() {
        return this.totalShown('alerts');
      },
      /** The aggregated alert total behind the lower chart, after the switches. */
      shownEvents() {
        return this.totalShown('events');
      },
      chartOptions() {
        return {
          title: { display: false },
          legend: { display: false },
          // Chart.js 2.9 axis shape. Not the v3 `scales: { x, y }` form.
          scales: {
            xAxes: [{ stacked: true, gridLines: { display: false }, categoryPercentage: 0.65, barPercentage: 0.9 }],
            yAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 }, gridLines: { color: '#eef0f2' } }]
          },
          tooltips: { mode: 'index', intersect: false },
          responsive: true,
          maintainAspectRatio: false
        };
      },
      severityRows() {
        const source = this.summary.by_severity || {};
        return SEVERITY_STACK_ORDER.map((key) => ({
          key,
          label: key === 'unscored' ? 'Not scored' : key,
          color: SEVERITY_COLORS[key],
          count: this.pickSeverity(source, key)
        }));
      },
      severityEmpty() {
        return this.severityRows.every((r) => !r.count) ? 'No changes in this period' : '';
      },
      highAlerts() {
        return this.pickSeverity(this.summary.by_severity || {}, 'HIGH');
      },
      safeCaption() {
        if (this.highAlerts > 0) {
          return `All ${this.formatNumber(this.highAlerts)} HIGH alerts in this period are still in the Alerts list. Grouping only adds a row; it never removes one. Whether an alert is also emailed is set by the notification mode in Settings.`;
        }
        return 'No HIGH alerts in this period. Grouping only adds a row to the Alerts list; it never removes one. Whether an alert is also emailed is set by the notification mode in Settings.';
      },
      topEvents() {
        const rows = Array.isArray(this.summary.top_events) ? this.summary.top_events : [];
        const biggest = rows.reduce((max, item) => Math.max(max, Number(item.alert_count) || 0), 1);
        return rows.map((item) => {
          const alertCount = Number(item.alert_count) || 0;
          return {
            id: item.id,
            site: item.site || '',
            summary: item.summary || `Change on ${item.site || 'a website'}`,
            severity: item.severity || 'unscored',
            severityMix: severityMix(item),
            categoryLabel: categoryLabel(item),
            alertCount,
            pageCount: item.page_count == null ? 0 : item.page_count,
            barWidth: `${Math.max(3, (100 * alertCount) / biggest)}%`
          };
        });
      },
      topEmpty() {
        return this.topEvents.length === 0 ? 'No changes in this period' : '';
      },
      ratioLabel() {
        return ratioLabel(this.summary.ratio);
      }
    },
    mounted() {
      this.fetchSummary();
    },
    methods: {
      /**
       * Builds one stacked bar dataset set for a day series.
       * @param {'alerts'|'events'} kind which count to read from each day
       * @returns {{labels: string[], datasets: object[]}}
       */
      buildStack(kind) {
        const datasets = SEVERITY_SERIES.filter((s) => this.visibleSeverities.indexOf(s.key) !== -1).map((s) => ({
          label: s.label,
          backgroundColor: SEVERITY_COLORS[s.key],
          data: this.byDay.map((d) => this.dayCount(d, kind, s.key)),
          stack: 'severity'
        }));
        return { labels: this.dayLabels, datasets };
      },
      /**
       * Adds up one kind of count across every day, for the severities that are
       * switched on.
       * @param {'alerts'|'events'} kind
       * @returns {number}
       */
      totalShown(kind) {
        return this.byDay.reduce(
          (sum, day) => sum + this.visibleSeverities.reduce((inner, key) => inner + this.dayCount(day, kind, key), 0),
          0
        );
      },
      /**
       * Reads one severity count out of a summary object. The API may spell the
       * keys in either case, so try the likely forms.
       * @param {object} source
       * @param {string} key
       * @returns {number}
       */
      pickSeverity(source, key) {
        if (!source) return 0;
        const candidates = [key, key.toLowerCase(), key.toUpperCase()];
        for (const name of candidates) {
          if (source[name] != null) return Number(source[name]) || 0;
        }
        return 0;
      },
      /**
       * Reads the count of alerts, or of aggregated alerts, of one severity for one
       * day.
       *
       * The API sends each day as:
       *   { date, alerts, events, by_severity: { HIGH: { alerts, events }, ... } }
       * so `day.alerts` is the day TOTAL and the split sits under `by_severity`.
       * A severity with no alerts that day is simply absent, which is why a
       * missing key must read as zero rather than as an error.
       *
       * The older nested and flat forms are still accepted, because this was
       * written before the API existed and a silent zero here would empty the
       * chart without saying why.
       *
       * @param {object} day one entry of `by_day`
       * @param {'alerts'|'events'} kind
       * @param {string} severity
       * @returns {number}
       */
      dayCount(day, kind, severity) {
        if (!day) return 0;

        // What the API actually sends.
        const split = day.by_severity || day.bySeverity;
        if (split && typeof split === 'object') {
          const bucket = this.pickSeverityBucket(split, severity);
          if (bucket && typeof bucket === 'object') return Number(bucket[kind]) || 0;
          // A bare number cannot say whether it counts alerts or groups. Using
          // it for both would draw the two charts identically and claim that
          // grouping achieved nothing, so it is not usable data here.
          return 0;
        }

        const nested = day[kind];
        if (nested && typeof nested === 'object') return this.pickSeverity(nested, severity);
        return this.pickSeverity(day, `${kind}_${severity}`);
      },
      /**
       * Finds one severity inside a by_severity object, whatever its letter case.
       * @param {object} source
       * @param {string} key
       * @returns {*}
       */
      pickSeverityBucket(source, key) {
        const candidates = [key, key.toLowerCase(), key.toUpperCase()];
        for (const name of candidates) {
          if (source[name] != null) return source[name];
        }
        return null;
      },
      formatNumber,
      /**
       * "~120/day" for a total over the days on screen.
       * @param {number|string} total
       * @returns {string}
       */
      perDayLabel(total) {
        const days = this.byDay.length;
        const num = Number(total);
        if (!days || !Number.isFinite(num) || num <= 0) return '';
        const perDay = num / days;
        return `~${perDay >= 10 ? this.formatNumber(Math.round(perDay)) : perDay.toFixed(1)} a day`;
      },
      async fetchSummary() {
        try {
          const response = await ChangeEventsService.fetchSummary({
            from: this.filterParams.from,
            to: this.filterParams.to
          });
          this.summary = { ...this.summary, ...(response || {}) };
        } catch (err) {
          this.$showMultiErrorMessage(err.messages);
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },
      onChangeDate(dates, dateStrings) {
        this.filterParams.from = dateStrings[0] ? this.getFilteredUTCStartDate(dateStrings[0], 'DD MMM YYYY') : null;
        this.filterParams.to = dateStrings[1] ? this.getFilteredUTCEndDate(dateStrings[1], 'DD MMM YYYY') : null;
        this.filterParams.fromLabel = dateStrings[0] || null;
        this.filterParams.toLabel = dateStrings[1] || null;
        this.fetchSummary();
      }
    }
  };
</script>

<style lang="scss" scoped>
  $card-border: #e3e7ea;
  $ink: #232a31;
  $ink-2: #5b6570;
  $muted: #8a94a0;

  .ce-note {
    color: $muted;
    font-size: 12px;
    line-height: 40px;
  }
  .ce-cardtitle {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: $muted;
    margin: 0 0 10px;
  }
  .ce-caption,
  .ce-empty {
    color: $ink-2;
    font-size: 12.5px;
    margin-top: 10px;
  }
  .ce-pipe {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(215px, 1fr));
    gap: 10px;

    &__step {
      position: relative;
      background: #ffffff;
      border: 1px solid $card-border;
      border-radius: 8px;
      padding: 14px 16px;

      &--inbox {
        background: #fffdf6;
        border-color: #efe2b8;
      }
    }
    &__step + &__step::before {
      content: '\203A';
      position: absolute;
      left: -12px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 22px;
      color: $muted;
      line-height: 1;
    }
    &__n {
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: $muted;
    }
    &__v {
      font-size: 30px;
      font-weight: 700;
      line-height: 1.15;
      margin-top: 2px;
      color: $ink;

      small {
        font-size: 13px;
        font-weight: 500;
        color: $ink-2;
      }
      &--pending {
        font-size: 18px;
        color: $ink-2;
      }
    }
    &__d {
      color: $ink-2;
      font-size: 12.5px;
      margin-top: 7px;
    }
  }
  .ce-pipenote {
    color: $ink-2;
    font-size: 12.5px;
    margin: 10px 2px 0;
    max-width: 900px;

    strong {
      color: $ink;
    }
  }
  .ce-safecard,
  .ce-panel {
    background: #ffffff;
    border: 1px solid $card-border;
    border-radius: 8px;
    padding: 16px 18px;
    margin-top: 14px;
  }
  .ce-safecard__big {
    font-size: 32px;
    font-weight: 700;
    line-height: 1.1;
    color: $ink;

    small {
      font-size: 14px;
      font-weight: 500;
      color: $ink-2;
    }
  }
  .ce-panellabel {
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: $ink-2;
    margin: 12px 0 2px;

    b {
      color: $ink;
    }
  }
  .ce-chart {
    position: relative;
    height: 120px;

    &--tall {
      height: 180px;
    }
  }
  .ce-chart-card {
    height: 320px;
  }
  .ce-sevlist {
    list-style: none;
    padding: 0;
    margin: 0;

    &__item {
      display: flex;
      align-items: center;
      padding: 10px 0;
      border-bottom: 1px solid #f0f0f0;
      font-size: 13px;
    }
    &__dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      margin-right: 8px;
    }
    &__label {
      flex: 1;
    }
    &__value {
      font-weight: 600;
    }
  }
  .ce-toplist {
    list-style: none;
    padding: 0;
    margin: 0;
    overflow-y: auto;

    &__item {
      padding: 9px 2px 7px;
      border-bottom: 1px solid #f0f2f4;

      &:last-child {
        border-bottom: none;
      }
    }
    &__line {
      display: flex;
      gap: 8px;
      align-items: baseline;
    }
    &__link {
      font-weight: 600;
      font-size: 13.5px;
      color: $ink;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        color: #20a4b8;
        text-decoration: underline;
      }
    }
    &__count {
      margin-left: auto;
      font-size: 12.5px;
      color: $ink-2;
      white-space: nowrap;
    }
    &__sub {
      color: $ink-2;
      font-size: 12px;
      margin: 1px 0 5px;
    }
    &__track {
      height: 4px;
      background: #eef1f4;
      border-radius: 2px;
    }
    &__fill {
      height: 100%;
      border-radius: 2px;
      background: #20a4b8;
      min-width: 3px;
    }
  }
  .ce-foot {
    margin-top: 22px;
    font-size: 12.5px;
    color: $muted;
  }
</style>
